/**
 * Reads the party ID the store should operate on from the iMIS-injected
 * `__ClientContext` element.
 *
 * Prefers `selectedPartyId` (the contact currently being viewed — set when
 * staff opens a member's record) and falls back to `loggedInPartyId` (the
 * authenticated user, on their own page). Returns null if the element is
 * missing/empty/unparseable, so the caller can render a sign-in note instead
 * of acting on the wrong party.
 */
export function readContextPartyId(): string | null {
  const element = document.getElementById("__ClientContext") as HTMLInputElement | null;
  if (!element || !element.value) return null;
  try {
    const ctx = JSON.parse(element.value) as {
      isAnonymous?: boolean;
      selectedPartyId?: string;
      loggedInPartyId?: string;
    };
    // iMIS still populates loggedInPartyId/selectedPartyId with a generic
    // anonymous-contact ID for logged-out visitors, so isAnonymous is the
    // real signal — a truthy party ID alone does not mean logged in.
    if (ctx.isAnonymous) return null;
    if (typeof ctx.selectedPartyId === "string" && ctx.selectedPartyId.length > 0) {
      return ctx.selectedPartyId;
    }
    if (typeof ctx.loggedInPartyId === "string" && ctx.loggedInPartyId.length > 0) {
      return ctx.loggedInPartyId;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Reads the **authenticated** user's party ID from `__ClientContext.loggedInPartyId`,
 * ignoring `selectedPartyId`. Used for the cart's CreatedBy/UpdatedBy audit fields
 * even when staff is acting on a member's record.
 */
export function readLoggedInPartyId(): string | null {
  const element = document.getElementById("__ClientContext") as HTMLInputElement | null;
  if (!element || !element.value) return null;
  try {
    const ctx = JSON.parse(element.value) as { isAnonymous?: boolean; loggedInPartyId?: string };
    if (ctx.isAnonymous) return null;
    return typeof ctx.loggedInPartyId === "string" && ctx.loggedInPartyId.length > 0
      ? ctx.loggedInPartyId
      : null;
  } catch {
    return null;
  }
}
